/**
 * Reply box on the ticket page: a Public reply / Internal note switch, the
 * shared rich-text editor with its own @mention search, image insert, and file
 * attachments.
 *
 * @mentions: typing `@` in the editor opens a people list. The editor writes
 * `<span data-mention-email="..." class="mention">@Name</span>` into its HTML,
 * which is sent as-is; the api reads those spans to notify the person.
 */

import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  FileUpload,
  RichTextEditor,
  mentionEmailsFromHtml,
  type MentionPerson,
  type UploadFile,
} from '@ampersandbrands/ui';
import { useAddComment } from '@/lib/useServiceDesk';
import { cockpitServiceDeskClient, sdHtmlForStorage } from '@/lib/serviceDeskClient';
import { uploadEditorImage } from '@/lib/editorImages';
import { fileToAttachment } from '@/lib/serviceDeskFiles';
import { SdError } from './SdShell';
import { SD_BTN_PRIMARY, SD_MUTED } from './sdStyles';

type Mode = 'public' | 'internal';

export function TicketComposer({ ticketId }: { ticketId: number }) {
  const [mode, setMode] = useState<Mode>('public');
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [files, setFiles] = useState<UploadFile[]>([]);
  // Remounts the editor after a send, because the editor only takes new content
  // from outside while it is not focused.
  const [editorKey, setEditorKey] = useState(0);
  const [sendError, setSendError] = useState<unknown>(null);
  const add = useAddComment(ticketId);

  const empty = text.trim().length === 0;
  const mentioned = useMemo(() => mentionEmailsFromHtml(html), [html]);

  const mentionSearch = useMemo(
    () => ({
      search: async (q: string): Promise<MentionPerson[]> => {
        const people = await cockpitServiceDeskClient.listPeople('all', q);
        return people
          .filter((p) => !!p.email)
          .map((p) => ({ id: p.id, name: p.name, email: p.email as string, title: p.title }));
      },
    }),
    [],
  );

  async function send() {
    if (empty) return;
    setSendError(null);
    try {
      const attachments = await Promise.all(files.map((f) => fileToAttachment(f.file)));
      await add.mutateAsync({
        visibility: mode,
        body_html: sdHtmlForStorage(html),
        body_text: text,
        attachments,
      });
      setHtml('');
      setText('');
      setFiles([]);
      setEditorKey((k) => k + 1);
    } catch (err) {
      setSendError(err);
    }
  }

  const internal = mode === 'internal';
  return (
    <section
      aria-label="Reply to this ticket"
      className={`rounded-lg border p-4 ${
        internal
          ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20'
          : 'border-amp-line bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface'
      }`}
    >
      <div role="radiogroup" aria-label="Who can see this" className="mb-3 flex gap-4 text-[13px]">
        {(
          [
            ['public', 'Public reply', 'The requester sees this and is emailed.'],
            ['internal', 'Internal note', 'Only IT and anyone you @mention see this.'],
          ] as const
        ).map(([value, label, hint]) => (
          <label key={value} className="flex cursor-pointer items-start gap-2">
            <input
              type="radio"
              name={`sd-mode-${ticketId}`}
              checked={mode === value}
              onChange={() => setMode(value)}
              className="mt-0.5"
            />
            <span>
              <span className="block font-semibold text-amp-ink dark:text-amp-dark-ink">{label}</span>
              <span className={`block text-[12px] ${SD_MUTED}`}>{hint}</span>
            </span>
          </label>
        ))}
      </div>

      <RichTextEditor
        key={editorKey}
        value={html}
        onChange={(h, t) => {
          setHtml(h);
          setText(t);
        }}
        mentions={mentionSearch}
        onInsertImage={uploadEditorImage}
        aria-label={internal ? 'Internal note' : 'Public reply'}
        className="min-h-[8rem]"
      />

      <div className="mt-3">
        <FileUpload
          files={files}
          onChange={setFiles}
          label="Attach files"
          maxFiles={10}
        />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void send()}
          disabled={empty || add.isPending}
          className={SD_BTN_PRIMARY}
        >
          {add.isPending && <Loader2 size={14} className="animate-spin" aria-hidden />}
          {internal ? 'Add internal note' : 'Send reply'}
        </button>
        {mentioned.length > 0 && (
          <span className={`text-[12px] ${SD_MUTED}`}>
            Notifying {mentioned.map((m) => m.name).join(', ')}
          </span>
        )}
      </div>
      <SdError error={sendError} what="send that" />
    </section>
  );
}
